// frontend/src/components/EducationDashboardFilters.jsx
// 教育進捗ダッシュボード用フィルタパネル + 再利用可能な MultiSelectDropdown
//
// フィルタ:
//   - 年月レンジ (from / to)
//   - 術式レベル (チェックボックス, procedure_levels を display_order 順)
//   - 必須区分 (必須 / 任意 / 全て) ... required_roles[].requirement の絞り込み
//   - 診療科 (検索+チェック multi-select)
//   - 術式 (検索+チェック multi-select, 診療科で絞り込みあり)
//   - 年次 (検索+チェック multi-select)
//   - 個人 (検索+チェック multi-select, スキル評価対象職種のみ)
//
// 親 (EducationDashboard) から master data を渡し、本コンポーネントは filter state の管理のみ。

import { useMemo, useRef, useState, useEffect } from "react";
import { Search, ChevronDown, X, Check } from "lucide-react";

// =============================================================================
// MultiSelectDropdown
// =============================================================================
export function MultiSelectDropdown({
  label,
  options,            // [{ value, label }]
  selected,           // Set
  onChange,           // (nextSet) => void
  placeholder = "全て",
  width = 220,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const kw = search.trim().toLowerCase();
    if (!kw) return options;
    return options.filter((o) => (o.label || "").toLowerCase().includes(kw));
  }, [options, search]);

  const toggle = (value) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value); else next.add(value);
    onChange(next);
  };
  const clear = (e) => { e.stopPropagation(); onChange(new Set()); };
  const selectAll = () => onChange(new Set(filtered.map((o) => o.value)));

  const count = selected.size;
  const displayText = count === 0 ? placeholder
    : count === options.length ? "全て"
    : `${count}件選択`;

  return (
    <div ref={ref} style={{ position: "relative", width }}>
      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <button type="button" onClick={() => setOpen((v) => !v)} style={styles.selectBtn}>
        <span style={{ flex: 1, textAlign: "left", color: count === 0 ? "#94a3b8" : "#0f172a" }}>
          {displayText}
        </span>
        {count > 0 && (
          <button type="button" onClick={clear} style={styles.clearBtn} title="クリア">
            <X size={12} />
          </button>
        )}
        <ChevronDown size={14} style={{ color: "#64748b" }} />
      </button>
      {open && (
        <div style={styles.dropdown}>
          <div style={styles.searchBox}>
            <Search size={12} style={{ color: "#94a3b8" }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="検索..."
              style={styles.searchInput}
              autoFocus
            />
          </div>
          <div style={styles.selectAllRow}>
            <button type="button" onClick={selectAll} style={styles.linkBtn}>全選択</button>
            <button type="button" onClick={() => onChange(new Set())} style={styles.linkBtn}>解除</button>
            <span style={{ marginLeft: "auto", fontSize: 10, color: "#94a3b8" }}>
              {count} / {options.length}
            </span>
          </div>
          <div style={styles.optionList}>
            {filtered.length === 0 && (
              <div style={{ padding: 10, fontSize: 11, color: "#94a3b8", textAlign: "center" }}>該当なし</div>
            )}
            {filtered.map((o) => {
              const on = selected.has(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggle(o.value)}
                  style={{
                    ...styles.optionItem,
                    background: on ? "#eff6ff" : "transparent",
                    color: on ? "#1d4ed8" : "#0f172a",
                  }}
                >
                  <span style={{
                    width: 14, height: 14, borderRadius: 3,
                    border: `1.5px solid ${on ? "#1d4ed8" : "#cbd5e1"}`,
                    background: on ? "#1d4ed8" : "#fff",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    {on && <Check size={10} color="#fff" />}
                  </span>
                  <span style={{ fontSize: 12, flex: 1, textAlign: "left" }}>{o.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Filter panel 本体
// =============================================================================
export default function EducationDashboardFilters({
  filters,
  onChange,
  departments = [],
  procedures = [],
  procedureLevels = [],
  staffList = [],
  yearOptions = [],
  availableMonths = [],
}) {
  const patch = (p) => onChange({ ...filters, ...p });

  const toggleLevelId = (id) => {
    const next = new Set(filters.levelIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    patch({ levelIds: next });
  };

  // 診療科で絞った術式リスト
  const procedureOptions = useMemo(() => {
    const scope = filters.deptIds.size === 0
      ? procedures
      : procedures.filter((p) => filters.deptIds.has(p.department_id));
    return scope.map((p) => ({
      value: p.id,
      label: `${p.name}`,
    }));
  }, [procedures, filters.deptIds]);

  const deptOptions = useMemo(
    () => departments.map((d) => ({ value: d.id, label: d.name })),
    [departments]
  );
  const yearSelectOptions = useMemo(
    () => yearOptions.map((y) => ({ value: String(y), label: `${y}年入職` })),
    [yearOptions]
  );
  const staffOptions = useMemo(
    () => staffList.map((s) => ({
      value: s.id,
      label: `${s.last_name || ""} ${s.first_name || ""} (${s.employee_id || ""})`,
    })),
    [staffList]
  );

  return (
    <div style={styles.panel}>
      <div style={styles.panelHeader}>
        <h2 style={styles.panelTitle}>分析条件</h2>
        <button type="button" onClick={() => onChange(makeDefaultFilters(procedureLevels))} style={styles.resetBtn}>
          条件をリセット
        </button>
      </div>

      {/* ===== 1. 年月レンジ (year/month ドロップダウン: データのある月のみ) ===== */}
      <div style={styles.sectionRow}>
        <div style={styles.fieldLabel}>年月</div>
        <YearMonthPicker value={filters.dateFrom} onChange={(v) => patch({ dateFrom: v })} availableMonths={availableMonths} />
        <span style={{ fontSize: 12, color: "#64748b" }}>〜</span>
        <YearMonthPicker value={filters.dateTo} onChange={(v) => patch({ dateTo: v })} availableMonths={availableMonths} />
        {availableMonths.length === 0 && (
          <span style={{ fontSize: 11, color: "#94a3b8" }}>(データ無し)</span>
        )}
      </div>

      {/* ===== 2 + 3. 術式レベル (チェックボックス) + 必須区分 (チェックボックス) を並列 ===== */}
      <div style={styles.sectionRow}>
        <div style={styles.fieldLabel}>術式レベル</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {procedureLevels.length === 0 && (
            <span style={{ fontSize: 11, color: "#94a3b8" }}>レベル未登録</span>
          )}
          {procedureLevels.map((lv) => {
            const on = filters.levelIds.has(lv.id);
            return (
              <label key={lv.id} style={pillStyle(on, lv.color || "#3b82f6")}>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggleLevelId(lv.id)}
                  style={{ margin: 0 }}
                />
                {lv.name}
              </label>
            );
          })}
        </div>

        <div style={{ ...styles.fieldLabel, marginLeft: 16 }}>必須区分</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {[
            { value: "required", label: "必須", color: "#ef4444" },
            { value: "optional", label: "任意", color: "#94a3b8" },
          ].map((r) => {
            const on = filters.requirements.has(r.value);
            return (
              <label key={r.value} style={pillStyle(on, r.color)}>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => {
                    const next = new Set(filters.requirements);
                    if (next.has(r.value)) next.delete(r.value); else next.add(r.value);
                    patch({ requirements: next });
                  }}
                  style={{ margin: 0 }}
                />
                {r.label}
              </label>
            );
          })}
        </div>

        <div style={{ ...styles.fieldLabel, marginLeft: 16 }}>役割</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {[
            { value: "scrub", label: "器械出し", color: "#0891b2" },
            { value: "circulating", label: "外回り", color: "#7c3aed" },
          ].map((r) => {
            const on = filters.roles?.has(r.value);
            return (
              <label key={r.value} style={pillStyle(on, r.color)}>
                <input
                  type="checkbox"
                  checked={!!on}
                  onChange={() => {
                    const next = new Set(filters.roles || []);
                    if (next.has(r.value)) next.delete(r.value); else next.add(r.value);
                    patch({ roles: next });
                  }}
                  style={{ margin: 0 }}
                />
                {r.label}
              </label>
            );
          })}
        </div>
      </div>

      {/* ===== 4. 診療科, 5. 術式, 6. 年次, 7. 個人 ===== */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <MultiSelectDropdown
          label="診療科"
          options={deptOptions}
          selected={filters.deptIds}
          onChange={(next) => patch({ deptIds: next })}
          width={220}
        />
        <MultiSelectDropdown
          label="術式"
          options={procedureOptions}
          selected={filters.procedureIds}
          onChange={(next) => patch({ procedureIds: next })}
          width={260}
        />
        <MultiSelectDropdown
          label="年次 (入職年)"
          options={yearSelectOptions}
          selected={filters.years}
          onChange={(next) => patch({ years: next })}
          width={180}
        />
        <MultiSelectDropdown
          label="個人"
          options={staffOptions}
          selected={filters.staffIds}
          onChange={(next) => patch({ staffIds: next })}
          width={260}
        />
      </div>
    </div>
  );
}

// =============================================================================
// Default filter state 作成ヘルパー (親からも使う)
// =============================================================================
export function makeDefaultFilters(procedureLevels = []) {
  const now = new Date();
  const yearNow = now.getFullYear();
  const monthNow = String(now.getMonth() + 1).padStart(2, "0");
  const yearAgo = yearNow - 1;
  return {
    dateFrom: `${yearAgo}-${monthNow}`,
    dateTo: `${yearNow}-${monthNow}`,
    levelIds: new Set(procedureLevels.map((l) => l.id)),
    requirements: new Set(["required", "optional"]),
    roles: new Set(["scrub", "circulating"]),
    deptIds: new Set(),
    procedureIds: new Set(),
    years: new Set(),
    staffIds: new Set(),
  };
}

// =============================================================================
// 年月ピッカー (availableMonths = "YYYY-MM" 配列 を「YYYY年M月」で表示)
// =============================================================================
function YearMonthPicker({ value, onChange, availableMonths = [] }) {
  const options = useMemo(() => {
    return availableMonths.map((v) => {
      const [y, m] = v.split("-");
      return { value: v, label: `${y}年${parseInt(m, 10)}月` };
    });
  }, [availableMonths]);
  // value が候補に無い場合に備えて option を追加 (壊れた表示を避ける)
  const hasValue = !value || options.some((o) => o.value === value);
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} style={styles.ymSelect}>
      <option value="">(未選択)</option>
      {!hasValue && value && (
        <option value={value}>
          {(() => { const [y, m] = value.split("-"); return `${y}年${parseInt(m, 10)}月 (データ無し)`; })()}
        </option>
      )}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// =============================================================================
// pillStyle helper (chip 風 label)
// =============================================================================
function pillStyle(on, color) {
  return {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "4px 10px", borderRadius: 20,
    border: `1.5px solid ${on ? color : "#cbd5e1"}`,
    background: on ? `${color}18` : "#fff",
    color: on ? color : "#64748b",
    fontSize: 12, fontWeight: 600, cursor: "pointer",
  };
}

// =============================================================================
// styles
// =============================================================================
const styles = {
  panel: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: 20,
    marginBottom: 16,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },
  panelHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 },
  panelTitle: { fontSize: 15, fontWeight: 700, color: "#0f172a", margin: 0 },
  resetBtn: {
    fontSize: 11, padding: "4px 10px",
    background: "#f1f5f9", color: "#334155",
    border: "1px solid #cbd5e1", borderRadius: 6, cursor: "pointer",
  },
  sectionRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  fieldLabel: {
    width: 90,
    fontSize: 12,
    fontWeight: 600,
    color: "#334155",
  },
  ymSelect: {
    padding: "6px 8px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 6,
    fontSize: 13,
    background: "#fff",
    minWidth: 120,
  },
  selectInput: {
    padding: "6px 10px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 6,
    fontSize: 13,
    minWidth: 120,
    background: "#fff",
  },
  selectBtn: {
    width: "100%",
    padding: "6px 10px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 6,
    background: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
  },
  clearBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#94a3b8",
    padding: 2,
    display: "inline-flex",
  },
  dropdown: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    background: "#fff",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
    zIndex: 100,
    maxHeight: 320,
    display: "flex",
    flexDirection: "column",
  },
  searchBox: {
    padding: "8px 10px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  searchInput: {
    border: "none",
    outline: "none",
    fontSize: 12,
    flex: 1,
    background: "transparent",
  },
  selectAllRow: {
    display: "flex",
    gap: 10,
    padding: "6px 10px",
    borderBottom: "1px solid #e2e8f0",
    alignItems: "center",
  },
  linkBtn: {
    background: "none",
    border: "none",
    color: "#1d4ed8",
    fontSize: 11,
    cursor: "pointer",
    padding: 0,
  },
  optionList: {
    overflowY: "auto",
    maxHeight: 220,
  },
  optionItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    padding: "6px 10px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    textAlign: "left",
  },
};
